import { useContext, useEffect, useMemo, useState } from 'react'
import { useRolePermissions } from '@domain/permissions'
import styled from '@emotion/styled'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import _ from 'lodash'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import { bool, func, number, oneOfType, string } from 'prop-types'
import { useDispatch } from 'react-redux'
import { CvsAutoComplete, CvsAvatar, CvsButton } from '@components'
import { Loader } from '@components/loader'
import { formatSelectedOptions } from '@components/select/cvs-autocomplete'
import { groupContext, userContext } from '@contexts'
import { roles } from '@global/constants'
import { showSnackbar } from 'functions'
import { t } from '@lingui/core/macro'
import { useAppSelector } from '@redux/hooks'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'

const CenterAvatar = styled.div`
  margin: auto;
  width: 50%;
  padding: 10px;
`

const Padding = styled.div`
  padding: 12px;
`

const BottomButton = styled(CvsButton)`
  margin-top: 12px;
`

const CenterTypography = styled(Typography)`
  text-align: center;
`

const SpaceTypography = styled(Typography)`
  margin-top: 4px;
  margin-bottom: 8px;
`

export const EditUser = ({ userId = null, onClose = () => {}, noEdit = false }) => {
  const { byId: userById, fetchUser, editUser } = useContext(userContext)
  const [loading, setLoading] = useState(false)
  const user = useMemo(() => userById[userId], [userById, userId])
  const { byId: groupById, fetchGroups } = useContext(groupContext)
  const [selectedGroups, setSelectedGroups] = useState([])
  const [role, setRole] = useState('')
  const [userPersistedPermissions, setUserPermissions] = useState([])
  const [permissions, setPermissions] = useState([])
  const [submitDisabled, setSubmitDisabled] = useState(false)
  const [newUser, setNewUser] = useState(null)
  const [updateUserName, setUpdateUserName] = useState('')
  const [userDisabled, setUserDisabled] = useState(false)
  const dispatch = useDispatch()

  const [ENABLE_LIVEBLOCKS = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.ENABLE_LIVEBLOCKS]),
  )

  const { data: permissionsOptions = [], isLoading: isPermissionsLoading } = useRolePermissions(
    role,
    {
      select: data => data.map(opt => ({ value: opt.slug, label: opt.name })),
    },
  )

  useEffect(() => {
    if (!userId) {
      setNewUser({})
      fetchGroups()
    }
  }, [])

  useEffect(() => {
    if (userId && userId !== 'null') {
      setLoading(true)
      fetchUser(userId).then(u => {
        setSelectedGroups(u.group_ids)
        setUserPermissions(_.uniq(u.permissions || []))
        setRole(u.role)
        setUpdateUserName(u.name)
        setLoading(false)
        setUserDisabled(u.disabled !== null)
      })
    }
  }, [fetchUser, setLoading, userId])

  const userPermissions = useMemo(() => {
    const availablePermissions = permissionsOptions.map(p => p.value)

    return userPersistedPermissions.filter(p => availablePermissions.includes(p))
  }, [userPersistedPermissions, permissionsOptions])

  useEffect(() => {
    setPermissions(userPermissions)
  }, [userPermissions, setPermissions])

  useEffect(() => {
    if (userId && userId !== 'null') {
      setLoading(true)
      fetchGroups().finally(() => {
        setLoading(false)
      })
    }
  }, [fetchGroups, setLoading, userId])

  const isValidUser = userObject =>
    !(
      isEmpty(userObject?.name?.trim()) ||
      isEmpty(userObject?.email?.trim()) ||
      isEmpty(userObject?.role)
    )

  const getUserObject = () => {
    const packageUser = userId ? user : newUser
    return {
      ...packageUser,
      permissions: _.uniq(permissions),
      role,
      group_ids: selectedGroups,
      name: updateUserName || newUser?.name,
    }
  }

  useEffect(() => {
    setSubmitDisabled(!isValidUser(getUserObject()))
  }, [role, newUser, updateUserName])

  const onSave = () => {
    setSubmitDisabled(true)
    const editUserObject = getUserObject()
    if (updateUserName) {
      editUserObject.name = updateUserName
    }
    editUser(editUserObject)
      .then(() => {
        onClose()
        setSubmitDisabled(false)
        dispatch(
          showSnackbar({
            message: t`User saved successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(res => {
        const message = res.response?.data?.message || t`User failed to save`
        onClose()
        setSubmitDisabled(false)
        dispatch(
          showSnackbar({
            message,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const groupOptions = Object.keys(groupById).map(groupId => ({
    label: groupById[groupId].name,
    value: groupId,
  }))

  const userEmail = user?.email
  const userLocation = user?.location
  const userLocationString = userLocation ? t`Location: ${userLocation}` : t`Location: Not set`

  let createdTimeString = ''
  if (user?.created_at) {
    const createdTime = moment.utc(user.created_at).local().fromNow()
    createdTimeString = t`Created: ${createdTime}`
  }
  let lastLoginTimeString = t`Last login: Never`
  if (user?.last_login) {
    const lastLoginTime = moment.utc(user.last_login).local().fromNow()
    lastLoginTimeString = t`Last login: ${lastLoginTime}`
  }

  return !user && !newUser ? (
    <CircularProgress color="primary" />
  ) : (
    <Padding>
      {user ? (
        <>
          <CenterAvatar>
            <CvsAvatar user={user} innerStyle={{ height: '160px', width: '160px' }} />
            <CenterTypography variant="body2">
              {user.name} {userDisabled ? t`(disabled)` : ''}
            </CenterTypography>
            <CenterTypography variant="body2">{user.title}</CenterTypography>
          </CenterAvatar>
          <SpaceTypography variant="h6">{t`About`}</SpaceTypography>
          <SpaceTypography variant="body1">{t`Email: ${userEmail}`}</SpaceTypography>
          <SpaceTypography variant="body1">{userLocationString}</SpaceTypography>
          <SpaceTypography variant="body1">{createdTimeString}</SpaceTypography>
          <SpaceTypography variant="body1">{lastLoginTimeString}</SpaceTypography>
        </>
      ) : (
        <>
          <TextField
            name="name"
            variant="outlined"
            label={t`Name`}
            margin="dense"
            required
            value={newUser.name}
            onChange={e => {
              setNewUser({ ...newUser, name: e.target.value })
            }}
            style={{ width: '100%' }}
            disabled={userDisabled}
          />
          <div style={{ marginTop: 12 }}>
            <TextField
              name="email"
              variant="outlined"
              label={t`Email`}
              margin="dense"
              required
              value={newUser.email}
              onChange={e => {
                setNewUser({ ...newUser, email: e.target.value })
              }}
              style={{ width: '100%' }}
              disabled={userDisabled}
            />
          </div>
        </>
      )}
      {!noEdit && (
        <>
          {loading ? (
            <Loader />
          ) : (
            <>
              {!newUser && (
                <TextField
                  name="name"
                  variant="outlined"
                  label={t`Name`}
                  margin="dense"
                  value={updateUserName}
                  onChange={e => {
                    setUpdateUserName(e.target.value)
                  }}
                  style={{ width: '100%' }}
                  disabled={userDisabled}
                />
              )}
              <CvsAutoComplete
                name="groups"
                margin="dense"
                label={t`Select groups`}
                isMulti
                options={groupOptions}
                onSelected={setSelectedGroups}
                selected={formatSelectedOptions(true, selectedGroups, groupOptions)}
                disabled={userDisabled}
              />
              <CvsAutoComplete
                name="role"
                margin="dense"
                label={t`Role`}
                required
                options={Object.keys(roles.listFormat).map(roleId => ({
                  label: roles.listFormat[roleId],
                  value: roles.api[roleId],
                }))}
                onSelected={setRole}
                selected={role}
                disabled={userDisabled}
              />
            </>
          )}
          {ENABLE_LIVEBLOCKS && role === roles.api.ADMIN && (
            <FormControlLabel
              name="approve-suggestions"
              control={
                <Checkbox
                  checked={permissions.includes('approve_suggestions')}
                  onChange={() => {
                    setPermissions(prev =>
                      prev.includes('approve_suggestions') ? [] : ['approve_suggestions'],
                    )
                  }}
                  value={permissions.includes('approve_suggestions')}
                  color="secondary"
                  disabled={userDisabled || user?.super_admin}
                />
              }
              label={t`Approve suggestions`}
            />
          )}
          {role === roles.api.MANAGER && (
            <>
              <CvsAutoComplete
                name="permissions"
                margin="dense"
                label={t`Permissions`}
                options={permissionsOptions}
                onSelected={setPermissions}
                selected={formatSelectedOptions(true, permissions, permissionsOptions)}
                isMulti
                disabled={userDisabled}
                loading={isPermissionsLoading}
              />
              <Typography variant="caption">
                {t`Managers have access to the selected permissions above and will be able to see and edit all of those items.`}
              </Typography>
            </>
          )}
          {role === roles.api.USER && (
            <FormControlLabel
              name="allow-submission-editing"
              control={
                <Checkbox
                  checked={permissions.includes('edit_submissions')}
                  onChange={() => {
                    setPermissions(prev =>
                      prev.includes('edit_submissions') ? [] : ['edit_submissions'],
                    )
                  }}
                  value={permissions.includes('edit_submissions')}
                  color="secondary"
                  disabled={userDisabled}
                />
              }
              label={t`Allow submission editing`}
            />
          )}
          <BottomButton
            name="save-user"
            fullWidth
            onClick={onSave}
            variant="contained"
            color="primary"
            disabled={submitDisabled || userDisabled}
            style={{ width: '100%' }}
          >
            {t`Save`}
          </BottomButton>
        </>
      )}
    </Padding>
  )
}

EditUser.propTypes = {
  userId: oneOfType([string, number]),
  onClose: func,
  noEdit: bool,
}
